import { Box, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { useEffect, useState } from "react";
import Button from "../../../../components/ui/Button";
import Input from "../../../../components/ui/Input";
import Text from "../../../../components/ui/Text";

export const AddPlayerDialog = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  players = [],
  editMode = false,
  selectedPlayerId = null,
}) => {
  const [newPlayerFirstName, setNewPlayerFirstName] = useState("");
  const [newPlayerLastName, setNewPlayerLastName] = useState("");
  const [initialData, setInitialData] = useState(null);

  // Check for duplicate names, considering that last name is optional
  const isNameDuplicate = players.some((player) => {
    // Skip the current player in edit mode
    if (editMode && selectedPlayerId && player.player_id === selectedPlayerId) {
      return false;
    }

    const playerFirstName = player.first_name?.trim().toLowerCase() || "";
    const playerLastName = player.last_name?.trim().toLowerCase() || "";
    const inputFirstName = newPlayerFirstName.trim().toLowerCase();
    const inputLastName = newPlayerLastName.trim().toLowerCase();

    // Both first names must match
    const firstNameMatches = playerFirstName === inputFirstName;

    // Last names must match (empty strings are considered matching)
    const lastNameMatches = playerLastName === inputLastName;

    const initialFirstName =
      initialData?.first_name?.trim().toLowerCase() || "";
    const initialLastName = initialData?.last_name?.trim().toLowerCase() || "";

    if (
      playerFirstName === initialFirstName &&
      playerLastName === initialLastName
    ) {
      return false;
    }

    return firstNameMatches && lastNameMatches;
  });

  const handlePlayerFirstNameChange = (event) => {
    setNewPlayerFirstName(event.target.value);
  };

  const handlePlayerLastNameChange = (event) => {
    setNewPlayerLastName(event.target.value);
  };

  const handleSubmit = () => {
    if (newPlayerFirstName.trim()) {
      onSubmit({
        first_name: newPlayerFirstName.trim(),
        last_name: newPlayerLastName.trim(),
      });
      setNewPlayerFirstName("");
      setNewPlayerLastName("");
    }
  };

  const handleClose = () => {
    onClose();
    setNewPlayerFirstName("");
    setNewPlayerLastName("");
  };

  useEffect(() => {
    if (!editMode || !selectedPlayerId) return;
    const selectedPlayer = players.find(
      (p) => p.player_id === selectedPlayerId,
    );

    if (selectedPlayer) {
      setNewPlayerFirstName(selectedPlayer.first_name || "");
      setNewPlayerLastName(selectedPlayer.last_name || "");
      setInitialData(selectedPlayer);
    }
  }, [editMode, selectedPlayerId]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: { sx: { borderRadius: "12px", m: 2, width: "310px" } },
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 1 }}>
        <Text variant="pageHeader" sx={{ mb: 0, fontSize: "20px" }}>
          {editMode ? "Edit Player" : "Add Player"}
        </Text>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Input
            label="First name"
            placeholder="First name"
            value={newPlayerFirstName}
            onChange={handlePlayerFirstNameChange}
            fullWidth
          />
          <Input
            label="Last name"
            placeholder="Last name"
            value={newPlayerLastName}
            onChange={handlePlayerLastNameChange}
            fullWidth
            error={isNameDuplicate}
            helperText={
              isNameDuplicate
                ? "A player with this name has already been added."
                : ""
            }
          />
        </Box>
      </DialogContent>

      <Box
        sx={{
          p: 3,
          pt: 1,
          gap: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!newPlayerFirstName.trim() || isNameDuplicate || loading}
          sx={{
            flex: 1,
            "&.Mui-disabled": {
              color: "#A1A1A1 !important",
              backgroundColor: "#F5F5F5 !important",
              border: "1px solid #D4D4D4",
            },
          }}
        >
          {loading
            ? editMode
              ? "Saving..."
              : "Adding..."
            : editMode
              ? "Save"
              : "Add"}
        </Button>
        <Button
          disabled={loading}
          variant="secondary"
          onClick={handleClose}
          sx={{ flex: 1 }}
        >
          Cancel
        </Button>
      </Box>
    </Dialog>
  );
};
